import React, { useState, useEffect, useCallback } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import SimpleImageUpload from "./SimpleImageUpload";
import ImageCropper from "./ImageCropper";
import ApiService from "../../services/ApiService";
import Utils from "../../services/Utils";
import { toast } from "react-toastify";
import { getImageFullUrl } from '../../services/ApiService'; // Utile pour la prévisualisation


const MAX_WIDTH = 170;
const MAX_HEIGHT = 187;

/**
 * PreconisationsModal Component
 *
 * Modal for uploading preco images for operation PLVs.
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Function} props.handleClose - Function to close the modal
 * @param {Object} props.operation - The operation object with operation_plvs_used
 * @param {Function} props.onSaveSuccess - Callback after successful save
 */

export default function PreconisationsModal({
  show,
  handleClose,
  operation,
  onSaveSuccess,
}) {
  const [precos, setPrecos] = useState({}); // Stocke soit le chemin string, soit le File
  const [initialPrecos, setInitialPrecos] = useState({});
  const [loading, setLoading] = useState(false);
  
  // NOUVEL ÉTAT pour gérer l'étape de recadrage
  const [imageToCrop, setImageToCrop] = useState(null); // Contient { plvId, imageSrc }

  // --- NOUVELLE FONCTION UTILITAIRE POUR L'APERÇU ---
    // Pour gérer l'affichage des images déjà existantes ou des images fraîchement recadrées
    const getMediaPreviewUrl = (mediaValue) => {
      if (mediaValue instanceof File) {
          return URL.createObjectURL(mediaValue);
      }
      if (typeof mediaValue === 'string' && mediaValue) {
          return getImageFullUrl(mediaValue);
      }
      return null;
    };
    // -------------------------------------------------

  /**
   * Initialize precos state when operation changes
   */
  useEffect(() => {
    if (operation && operation.operation_plvs_used) {
      const initialData = {};
      operation.operation_plvs_used.forEach((plv) => {
        // Le preco initial est une chaîne de caractères (chemin du fichier déjà uploadé)
        initialData[plv.id] = plv.preco || null; 
      });
      setPrecos(initialData);
      setInitialPrecos(initialData);
    }
  }, [operation]);


  /**
   * 1. Gère le fichier sélectionné par l'utilisateur.
   * Déclenche l'outil de recadrage.
   * @param {number} plvId - The PLV ID
   * @param {File|null} fileOrNull - Le nouvel objet File sélectionné (ou null si suppression)
   */
  const handleFileSelect = (plvId, fileOrNull) => {
    if (fileOrNull && fileOrNull instanceof File) {
      // 1. Lire le fichier sélectionné comme Base64 pour l'afficher dans le cropper
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageToCrop({ 
          plvId: plvId, 
          imageSrc: e.target.result // L'image Base64 pour le cropper
        });
      };
      reader.readAsDataURL(fileOrNull);
      
    } else if (fileOrNull === null) {
      // Cas de suppression : met à jour l'état directement
      setPrecos((prev) => ({
        ...prev,
        [plvId]: null, // Supprimer l'image (ancien chemin)
      }));
    }
    // Si un nouveau fichier est sélectionné, on attend le recadrage pour mettre à jour 'precos'
  };
  
  /**
   * 2. Gère le fichier final après recadrage.
   * @param {File} croppedFile - Le fichier image final (Blob converti en File).
   */
  const handleCropComplete = useCallback((croppedFile) => {
    const plvId = imageToCrop.plvId;
    
    // Met à jour l'état avec le nouveau Fichier binaire pour l'envoi
    setPrecos((prev) => ({
      ...prev,
      [plvId]: croppedFile,
    }));
    
    // Ferme le cropper
    setImageToCrop(null);
  }, [imageToCrop]);
  
  /**
   * Annulation du recadrage
   */
  const handleCropCancel = () => {
    setImageToCrop(null);
  };


  /**
   * Handle form submission - save all precos
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Filter PLVs qui ont été modifiés (nouveau fichier, suppression ou changement de string)
    const requestsToSend = operation.operation_plvs_used.map((plv) => {
      const plvId = plv.id;
      const precoValue = precos[plvId];
      const initialPrecoValue = initialPrecos[plvId];
      
      // Cas 1 : Aucune modification ou image initiale conservée (chemin string)
      if (precoValue === initialPrecoValue && typeof precoValue === 'string') {
          return null; 
      }
      
      // Préparation des données FormData pour l'upload binaire
      const formData = new FormData();
      formData.append('operation_type_id', operation.id);
      formData.append('operation_plv_type_id', plvId);
      
      if (precoValue instanceof File) {
          // Cas 2 : Nouveau fichier binaire recadré à uploader
          formData.append('preco_file', precoValue); 
          formData.append('preco', 'FILE_UPLOADED'); // Flag pour le backend si besoin
      } else if (precoValue === null && initialPrecoValue !== null) {
          // Cas 3 : Suppression demandée (ancien fichier existait et est maintenant null)
          // On envoie une requête avec le champ vide pour demander la suppression au backend
          formData.append('preco', ''); 
      } else {
          // Autres cas, si le fichier était déjà null et reste null
          return null;
      }
      
      // Retourne la Promesse d'API
      return ApiService.request(
        formData,
        "operation-precos",
        "post",
        {
          headers: {
            'Content-Type': 'multipart/form-data', // IMPORTANT pour FormData
          }
        }
      );
    }).filter(p => p !== null);

    // Execute all requests
    try {
      await Promise.all(requestsToSend);
      Utils.notifySuccess(`Enregistrement des préconisations`);
      setLoading(false);
      handleClose();
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      console.error(error);
      Utils.notifyError("de l'enregistrement des préconisations");
      setLoading(false);

      if (error.response?.data?.message === "Unauthenticated.") {
        localStorage.clear();
      }
    }
  };

  if (!operation) return null;
  
  // AFFICHER LE CROPPER SI UNE IMAGE EST EN ATTENTE
  if (imageToCrop) {
      return (
          <Modal show={show} size="xl" onHide={handleCropCancel} centered>
              <Modal.Header closeButton>
                  <Modal.Title>Recadrer l'image</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                  <div style={{ height: '500px' }}>
                      <ImageCropper 
                          imageSrc={imageToCrop.imageSrc}
                          targetWidth={MAX_WIDTH}
                          targetHeight={MAX_HEIGHT}
                          onComplete={handleCropComplete}
                          onCancel={handleCropCancel}
                      />
                  </div>
              </Modal.Body>
          </Modal>
      );
  }


  // Rendu normal du MODAL D'UPLOAD
  return (
    <Modal show={show} size="lg" onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Ajouter une préconisation sur une opération «{" "}
          <span style={{ color: operation.color }}>{operation.name}</span> »
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {operation.operation_plvs_used && operation.operation_plvs_used.length > 0 ? (
            <div>
              {operation.operation_plvs_used.map((plv) => (
                <div key={plv.id} className="mb-4">
                  <Form.Group>
                    <Form.Label>
                      <strong>{plv.name}</strong>
                    </Form.Label>
                    <SimpleImageUpload
                      // Si c'est un File (recadré), affiche l'aperçu via son URL objet, sinon utilise la string (chemin API)
                      //value={precos[plv.id] instanceof File ? URL.createObjectURL(precos[plv.id]) : precos[plv.id]}
                      value={getMediaPreviewUrl(precos[plv.id])}
                      onChange={(file) => handleFileSelect(plv.id, file)} // Attend maintenant un objet File ou null
                      buttonLabel="Sélectionner un fichier"
                      disabled={loading}
                      // RETIRER LES RESTRICTIONS DE TAILLE ICI
                      // maxImageWidth={170} 
                      // maxImageHeight={187}
                    />
                  </Form.Group>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted py-4">
              Aucun PLV associé à cette opération.
            </div>
          )}
          <div className="grey pt-3">Formats autorisés: jpg, png</div>
          <div className="grey">**Les images seront redimensionnées et recadrées à 170x187px.**</div>
          <div className="grey">Proportion largeur/hauteur: 1/1,1</div>
          <div className="grey pb-3">Résolution conseillée: 300px/pouces</div>
        </Modal.Body>
        <Modal.Footer className="is-bottom-right">
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            variant="success"
            type="submit"
            disabled={loading || operation.operation_plvs_used?.length === 0}
          >
            {loading ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}